import type { TabsProps } from "antd";
import { Alert, Button, Skeleton, Tabs } from "antd";
import { CreditCard, FileText, Link2, Shield, Store } from "lucide-react";
import { useEffect, useState } from "react";

import Modal from "@/components/Modal";

import {
  StoreBankTab,
  StoreBasicTab,
  StoreContractTab,
  StoreHeaderCard,
  StoreInfoTab,
  StoreLicenseTab,
} from "./detailTabs";
import { useShopDetail } from "./hooks/useShopDetail";

/** 门店详情 Modal props */
export interface StoreDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** 门店 ID，打开时拉取详情 */
  shopId: string | null;
  /** 打开归属上级为代理时的详情 */
  onOpenAgent?: (agentId: string) => void;
  /** 打开归属上级为门店时的详情 */
  onOpenStore?: (storeId: string) => void;
  /** 打开关联主体详情 */
  onOpenEntity?: (entityId: string) => void;
  /** 打开关联账号详情 */
  onOpenAccount?: (accountId: string) => void;
}

/**
 * 门店详情 Modal：信息名片 + 五个 Tab，支持编辑模式双击字段
 */
export const StoreDetailModal = ({
  open,
  onClose,
  shopId,
  onOpenAgent,
  onOpenStore,
  onOpenEntity,
  onOpenAccount,
}: StoreDetailModalProps) => {
  /** 当前 Tab key */
  const [activeTab, setActiveTab] = useState("basic");
  // const [isEditMode, setIsEditMode] = useState(false);
  /** 编辑模式（功能暂时关闭） */
  const isEditMode = false;
  const { shop, loading, editShop } = useShopDetail(shopId, open);

  /** 关闭时重置 Tab */
  useEffect(() => {
    if (open) return;
    setActiveTab("basic");
  }, [open]);

  const tabCtx = {
    shop: shop!,
    isEditMode,
    onEditShop: editShop,
    onOpenAgent,
    onOpenStore,
    onOpenEntity,
    onOpenAccount,
  };

  const tabItems: TabsProps["items"] = shop
    ? [
        {
          key: "basic",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Store className="w-3.5 h-3.5" />
              基本信息
            </span>
          ),
          children: <StoreBasicTab {...tabCtx} />,
        },
        {
          key: "license",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" />
              证照管理
            </span>
          ),
          children: <StoreLicenseTab {...tabCtx} />,
        },
        {
          key: "bank",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <CreditCard className="w-3.5 h-3.5" />
              银行与开票
            </span>
          ),
          children: <StoreBankTab {...tabCtx} />,
        },
        {
          key: "contract",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" />
              合同与其他
            </span>
          ),
          children: <StoreContractTab {...tabCtx} />,
        },
        {
          key: "related",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Link2 className="w-3.5 h-3.5" />
              关联信息
            </span>
          ),
          children: <StoreInfoTab {...tabCtx} />,
        },
      ]
    : [];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={700}
      centered
      title={isEditMode ? "编辑门店详情" : "门店详情"}
      titleIcon={<Store className="w-5 h-5 text-orange-500" />}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>关闭</Button>
          {/* 编辑功能暂时注释
          {!isEditMode ? (
            <Button
              type="primary"
              icon={<Pencil className="w-3.5 h-3.5" />}
              disabled={!shop}
              onClick={() => setIsEditMode(true)}
            >
              编辑
            </Button>
          ) : (
            <Button type="primary" disabled={!shop} onClick={() => setIsEditMode(false)}>
              完成编辑
            </Button>
          )}
          */}
        </div>
      }
      styles={{ content: { height: "80vh" } }}
    >
      {loading ? (
        <div className="space-y-4">
          <Skeleton active avatar paragraph={{ rows: 2 }} />
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      ) : shop ? (
        <>
          <StoreHeaderCard shop={shop} />
          {isEditMode ? (
            <Alert
              className="mt-2!"
              message="双击字段可编辑，修改后将自动保存"
              type="info"
              showIcon
            />
          ) : null}
          <Tabs className="mt-2" activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
        </>
      ) : (
        <div className="py-12 text-center text-sm text-slate-400">暂无门店详情</div>
      )}
    </Modal>
  );
};
